import * as React from 'react';
import { useState } from 'react';
import { SectionCard, Select } from '../../../components/forms';
import { IconExternal } from '../gallery/parts';
import { ATTIO, AsyncConfirm, CONTACT_ROLES, label, ProductionApi, reason, RemoveButton, useInflight } from './parts';

/* Client contacts on the event — add and remove each save on their own. */

export default function ContactsSection({
    api,
    activationId,
    contacts,
    directory,
    directoryFailed,
    refresh,
    toast,
}: {
    api: ProductionApi;
    activationId: number;
    contacts: any[];
    directory: any[];
    directoryFailed: boolean;
    refresh: () => Promise<void>;
    toast: (text: string) => void;
}) {
    const { busy, run } = useInflight();
    const [pick, setPick] = useState('');
    const [role, setRole] = useState('primary');
    const [error, setError] = useState<string | null>(null);
    const [removing, setRemoving] = useState<any | null>(null);
    const [removeError, setRemoveError] = useState<string | null>(null);

    /* local contacts only (Attio-only search hits carry "attio:" ids) */
    const options = directory.filter((c) => Number.isFinite(+c.id));
    const already = (c: any) => contacts.some((x) => +x.id === +c.id && x.role === role);

    const add = async () => {
        if (!pick) return;
        const c = options.find((x) => +x.id === +pick);
        if (c && already(c)) {
            setError(`${c.name} is already the ${label(role).toLowerCase()} contact.`);
            return;
        }
        setError(null);
        const r = await run('add', () => api.addActivationContact(activationId, { contactId: +pick, role }));
        if (r.ok) {
            setPick('');
            setRole('primary');
            await refresh();
            toast(`${c?.name ?? 'Contact'} added.`);
        } else if (!('skipped' in r && r.skipped)) {
            setError(reason(r.error, `Couldn’t add ${c?.name ?? 'that contact'} — try again.`));
        }
    };

    const remove = async () => {
        if (!removing) return;
        const c = removing;
        setRemoveError(null);
        const r = await run(`remove-${c.activationContactId}`, () => api.removeActivationContact(+c.activationContactId));
        if (r.ok) {
            setRemoving(null);
            await refresh();
            toast(`${c.name} removed from this event.`);
        } else if (!('skipped' in r && r.skipped)) {
            setRemoveError(reason(r.error, `Couldn’t remove ${c.name} — try again.`));
        }
    };

    return (
        <SectionCard title="Contacts" note={contacts.length ? String(contacts.length) : undefined}>
            {contacts.length === 0 ? (
                <p className="quiet-empty">No client contacts yet.</p>
            ) : (
                contacts.map((c) => (
                    <div className="trow2 prd-row" key={c.activationContactId ?? `${c.id}-${c.role}`}>
                        <span className="prd-main">
                            <span className="nm2">
                                {c.name}
                                {c.role && <span className="prd-tag prd-inline">{label(c.role)}</span>}
                            </span>
                            <span className="sub2 prd-trunc">{[c.email, c.phone, c.companyName].filter(Boolean).join(' · ')}</span>
                        </span>
                        <span className="prd-ctl">
                            {c.attioId && (
                                <a className="gl-ic" href={`${ATTIO}/people/record/${c.attioId}/overview`} target="_blank" rel="noreferrer" aria-label={`${c.name} in Attio`} title="Open in Attio">
                                    <IconExternal />
                                </a>
                            )}
                            <RemoveButton
                                label={`Remove ${c.name} from this event`}
                                disabled={!c.activationContactId || !!busy[`remove-${c.activationContactId}`]}
                                onClick={() => {
                                    setRemoveError(null);
                                    setRemoving(c);
                                }}
                            />
                        </span>
                    </div>
                ))
            )}
            {directoryFailed ? (
                <p className="field-e">Couldn’t load contacts, so none can be added. Reload to try again.</p>
            ) : (
                <div className="prd-add">
                    <span className="prd-grow">
                        <Select aria-label="Contact to add" value={pick} disabled={!!busy.add} onChange={(e) => setPick(e.target.value)}>
                            <option value="">Add contact…</option>
                            {options.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                    {c.companyName ? ` — ${c.companyName}` : ''}
                                </option>
                            ))}
                        </Select>
                    </span>
                    {pick && (
                        <span className="prd-role">
                            <Select aria-label="Contact role" value={role} disabled={!!busy.add} onChange={(e) => setRole(e.target.value)}>
                                {CONTACT_ROLES.map((r) => (
                                    <option key={r} value={r}>
                                        {label(r)}
                                    </option>
                                ))}
                            </Select>
                        </span>
                    )}
                    <button type="button" className="tb-new" disabled={!pick || !!busy.add} onClick={add}>
                        {busy.add ? 'Adding…' : 'Add'}
                    </button>
                </div>
            )}
            {error && (
                <p className="field-e" role="alert">
                    {error}
                </p>
            )}

            <AsyncConfirm
                open={!!removing}
                title={`Remove ${removing?.name ?? ''} from this event?`}
                actionLabel="Remove"
                busyLabel="Removing…"
                busy={!!(removing && busy[`remove-${removing.activationContactId}`])}
                error={removeError}
                danger
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            >
                <p>
                    They’re no longer the {removing?.role ? label(removing.role).toLowerCase() : ''} contact here. The contact stays in Contacts
                    {removing?.attioId ? ' and in Attio' : ''}.
                </p>
            </AsyncConfirm>
        </SectionCard>
    );
}
